import type {FC} from 'react';
import {
  Box,
  Button,
  Icon,
  IconNames,
  Stack,
  Typography,
} from '@wellstone-solutions/web';
import {Events, Theme, Hooks} from '@wellstone-solutions/common';
import type {UICalendarEventType} from '@wellstone-solutions/common';
import {useStores} from 'hooks/useStores';
import {toFullUrl} from 'utils/toFullUrl';
import {buildCityStateZip, getDirectionsUrl} from 'utils/addressHelpers';
import {parseMeetingLink} from 'utils/parseMeeting';

const {useAddress} = Hooks;

export const GroupEventLocation: FC<UICalendarEventType> = ({
  calendarEvent,
}) => {
  const {eventStore} = useStores();
  const {location, link} = calendarEvent.data;
  const {
    displayCityStateZip,
    displayFullAddress,
    displayStreetAddress,
    hasCityOrStateOrZip,
    hasStreetAddress,
  } = useAddress(location);

  const parsedMeeting = parseMeetingLink(link);

  return (
    <Stack
      sx={{
        width: {
          xs: '100%',
          md: '50%',
        },
      }}>
      {displayFullAddress.length > 0 && (
        <Button
          disableRipple
          sx={{
            p: 0,
            textAlign: 'left',
            '&:hover': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          }}
          onClick={() => {
            eventStore.addEvent(Events.VISITED_CALENDAR_ADDRESS, {
              calendar_event_id: calendarEvent.id,
              original_start: calendarEvent.originalStart,
            });
            window.open(
              getDirectionsUrl(buildCityStateZip(location || {}, true)),
              '_blank',
            );
          }}>
          <Stack direction="row" sx={{flex: 1, alignItems: 'flex-start'}}>
            <Icon
              name={IconNames.MapPin}
              size={28}
              color={Theme.colorPalette.primary}
              style={styles.icon}
            />
            <Stack sx={{ml: 1, mb: 1, color: Theme.colorPalette.darkest}}>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: Theme.colorPalette.blue,
                }}>
                {location.name}
              </Typography>
              {hasStreetAddress && (
                <Typography>{displayStreetAddress}</Typography>
              )}
              {hasCityOrStateOrZip && (
                <Typography>{displayCityStateZip}</Typography>
              )}
            </Stack>
          </Stack>
        </Button>
      )}
      {!!link && (
        <Button
          disableRipple
          variant="text"
          sx={styles.linkButton}
          name={'meeting-link'}
          color="primary"
          onClick={() => {
            eventStore.addEvent(Events.VISITED_CALENDAR_LINK, {
              calendar_event_id: calendarEvent.id,
              original_start: calendarEvent.originalStart,
            });

            window.open(toFullUrl(parsedMeeting.url), '_blank');
          }}>
          <Stack direction="row" spacing={1}>
            <Icon
              name="MapPin"
              color={Theme.colorPalette.dark}
              size={28}
              style={styles.icon}
            />

            <Box sx={{width: 'calc(100% - 28px)'}}>
              <Typography sx={styles.linkText}>{link}</Typography>

              <Typography sx={{color: Theme.colorPalette.darkest}}>
                Virtual meeting
              </Typography>
            </Box>
          </Stack>
        </Button>
      )}
    </Stack>
  );
};

const styles = {
  buttonStyle: {maxWidth: '300px', marginTop: '8px'},
  icon: {
    flexShrink: 0,
  },
  linkButton: {
    padding: 0,
    textAlign: 'left',
    display: 'block',
    width: '100%',
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
  },
  linkText: {
    fontSize: 18,
    color: Theme.colorPalette.blue,
    fontWeight: 'bold',
    display: '-webkit-box',
    WebkitLineClamp: 3 /* Number of lines */,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};
